import Alert from "@mui/material/Alert";
import { useForm } from "react-hook-form";
import useFormField from "../hooks/useFormField";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";
import CompactNumberInput from "../ui/CompactNumberInput";
import Button from "@mui/material/Button";
import React from "react";
import CollectionDialog from "./CollectionDialog";
import QuantityWheel from "./QuantityWheel";
import useCollectionSubmission from "../hooks/useCollectionSubmission";
import CollectionHelp from "./CollectionHelp";
import { describeApiError } from "../api/request";
import {
  MAX_QUANTITY,
  parseCollectionQuantity,
  removalChange,
} from "../utils/collection";
const CHANGE_LABELS = {
  local: "remove from BPMBinder only",
  discogs: "remove from BPMBinder and Discogs",
  quantity: "update quantity in BPMBinder and Discogs",
  quantityLocal: "update quantity in BPMBinder only",
  restore: "restore one copy in BPMBinder and Discogs",
  restoreLocal: "restore one copy in BPMBinder only",
};
export default function CollectionActions({
  album,
  settings,
  onChanged,
  disabled = false,
  requestedMode = null,
  onDialogClose,
  menuControlled = false,
}) {
  const form = useForm({
    defaultValues: {
      quantity: String(album.quantity),
      copiesToRemove: 1,
      discogsChoice: null,
      remember: false,
    },
    mode: "onChange",
  });
  const [dialog, setDialog] = React.useState(null);
  const [quantity, setQuantity] = useFormField(form, "quantity", {
    validate: (value) =>
      dialog !== "quantity" ||
      parseCollectionQuantity(value) !== null ||
      "Enter a whole number from 1 to 2147483647. Use Delete release to remove copies.",
  });
  const [copiesToRemove, setCopiesToRemove] = useFormField(
    form,
    "copiesToRemove",
  );
  const validation = form.formState.errors.quantity?.message;
  const { clearErrors } = form;
  const [notice, setNotice] = React.useState("");
  const [discogsChoice, setDiscogsChoice] = useFormField(form, "discogsChoice");
  const [remember, setRemember] = useFormField(form, "remember");
  const [removalStep, setRemovalStep] = React.useState("choice");
  const close = (completed = false) => {
    setDialog(null);
    onDialogClose?.(completed);
  };
  const action = useCollectionSubmission(settings, (result) => {
    close(true);
    setNotice(
      result.archive
        ? "Release moved to Archived Releases."
        : "Collection updated.",
    );
    onChanged(result);
  });
  React.useEffect(() => {
    if (requestedMode) {
      setQuantity(String(album.quantity));
      setCopiesToRemove(1);
      clearErrors();
      setDiscogsChoice(null);
      setRemember(false);
      setRemovalStep("choice");
      setDialog(requestedMode);
    }
  }, [
    requestedMode,
    album.quantity,
    setQuantity,
    setCopiesToRemove,
    setDiscogsChoice,
    setRemember,
    clearErrors,
  ]);
  const open = (mode) => {
    clearErrors();
    setDiscogsChoice(null);
    setRemember(false);
    setRemovalStep("choice");
    setQuantity(String(album.quantity));
    setCopiesToRemove(1);
    setDialog(mode);
  };
  const preference = settings?.syncPreference;
  React.useEffect(() => {
    setDiscogsChoice(null);
    setRemovalStep("choice");
  }, [preference, setDiscogsChoice]);
  const needsQuantityStep =
    dialog === "remove" && album.quantity > 1 && preference === "ask";
  const showNext = needsQuantityStep && removalStep === "choice";
  const showDiscogsChoice =
    preference === "ask" && !(needsQuantityStep && removalStep === "quantity");
  const changeRemovalStep = (event, step) => {
    setRemovalStep(step);
    event.currentTarget.closest('[role="dialog"]').focus();
  };
  const syncWithDiscogs =
    preference === "always"
      ? true
      : preference === "never"
        ? false
        : preference === "ask"
          ? discogsChoice
          : null;
  const submit = () => {
    if (
      !settings?.ready ||
      action.pending ||
      typeof syncWithDiscogs !== "boolean"
    )
      return;
    const albumId = Number(album.id);
    const rememberedPreference =
      preference === "ask" && remember
        ? syncWithDiscogs
          ? "always"
          : "never"
        : undefined;
    if (dialog === "remove") {
      action.run(
        removalChange(albumId, album.quantity, copiesToRemove, syncWithDiscogs),
        rememberedPreference,
      );
    } else if (dialog === "quantity") {
      const next = parseCollectionQuantity(quantity);
      if (next === null) return;
      action.run(
        {
          albumId,
          action: syncWithDiscogs ? "quantity" : "quantityLocal",
          quantity: next,
        },
        rememberedPreference,
      );
    } else {
      action.run(
        {
          albumId,
          action: syncWithDiscogs ? "restore" : "restoreLocal",
        },
        rememberedPreference,
      );
    }
  };
  const frozen = action.pending || !!action.error;
  const help =
    dialog === "remove"
      ? "Removing all copies archives the release. Including Discogs also removes its copies and their notes."
      : dialog === "restore"
        ? "Restores one BPMBinder copy. Including Discogs adds a copy only if none exists."
        : "Sets your copy count. Including Discogs also adds or removes copies there.";
  return (
    <div className="collection-actions">
      {notice && <p role="status">{notice}</p>}
      {!menuControlled &&
        (action.error ? (
          <Button
            className="btn"
            onClick={() => open("recovery")}
            disabled={action.pending}
          >
            Resume failed collection change
          </Button>
        ) : album.archive ? (
          <Button
            className="btn"
            onClick={() => open("restore")}
            disabled={disabled}
          >
            Restore release
          </Button>
        ) : (
          <>
            <Button
              className="btn"
              onClick={() => open("remove")}
              disabled={disabled}
            >
              Delete release
            </Button>
            <Button
              className="btn"
              onClick={() => open("quantity")}
              disabled={disabled}
            >
              Update quantity
            </Button>
          </>
        ))}
      {dialog && (
        <CollectionDialog
          title={
            action.error
              ? action.rememberFailed
                ? "Save selection"
                : "Retry collection change"
              : dialog === "restore"
                ? "Restore release"
                : dialog === "quantity"
                  ? "Update quantity"
                  : "Delete release?"
          }
          pending={action.pending}
          onClose={() => close()}
        >
          <div className="collection-dialog-release">
            <p>{album.title}</p>
            {!action.error && <CollectionHelp>{help}</CollectionHelp>}
          </div>
          {action.error ? (
            <Alert role="alert" severity="error">
              <p>{describeApiError(action.error)}</p>
              {!action.rememberFailed && (
                <>
                  <p>
                    Original change: {CHANGE_LABELS[action.request.action]}
                    {action.request.quantity !== undefined
                      ? ` to ${action.request.quantity} copies`
                      : ""}
                    .
                  </p>
                  <p>
                    {["discogs", "quantity", "restore"].includes(
                      action.request.action,
                    )
                      ? "Discogs may already have changed. "
                      : ""}
                    Retry sends the same original request, even if your settings
                    have changed.
                  </p>
                </>
              )}
            </Alert>
          ) : (
            <>
              {dialog === "remove" && album.quantity > 1 && !showNext && (
                <QuantityWheel
                  maximum={album.quantity}
                  value={copiesToRemove}
                  onChange={setCopiesToRemove}
                  disabled={frozen}
                />
              )}
              {dialog === "quantity" && (
                <label className="collection-quantity">
                  New quantity
                  <CompactNumberInput
                    aria-label="New quantity"
                    type="number"
                    min="1"
                    max={MAX_QUANTITY}
                    step="1"
                    value={quantity}
                    disabled={frozen}
                    onChange={(event) => {
                      setQuantity(event.target.value);
                    }}
                  />
                </label>
              )}
              {dialog === "restore" && <p>Restore 1 copy.</p>}
              {!settings?.ready ? (
                settings?.error || settings?.saveError ? (
                  <Alert role="alert" severity="error">
                    {describeApiError(settings.error || settings.saveError)}{" "}
                    <Button className="btn" onClick={settings.reload}>
                      Retry settings
                    </Button>
                  </Alert>
                ) : (
                  <p role="status">
                    {action.pending ? "Saving…" : "Loading settings…"}
                  </p>
                )
              ) : showDiscogsChoice ? (
                <FormControl
                  component="fieldset"
                  className="collection-discogs-choice"
                  disabled={frozen}
                  fullWidth
                  sx={{ alignItems: "center" }}
                >
                  <FormLabel
                    component="legend"
                    sx={{ "&.Mui-focused": { color: "text.secondary" } }}
                  >
                    {dialog === "remove"
                      ? "Also remove from Discogs?"
                      : dialog === "restore"
                        ? "Also add to Discogs?"
                        : "Also update Discogs?"}
                  </FormLabel>
                  <RadioGroup
                    row
                    name="discogs-choice"
                    value={
                      discogsChoice === null ? "" : discogsChoice ? "yes" : "no"
                    }
                    onChange={(_, value) => setDiscogsChoice(value === "yes")}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                  <FormControlLabel
                    className="collection-remember"
                    control={
                      <Checkbox
                        checked={remember}
                        onChange={(event) => setRemember(event.target.checked)}
                      />
                    }
                    label="Remember my selection"
                  />
                </FormControl>
              ) : (
                <p className="collection-scope">
                  Discogs: {syncWithDiscogs ? "Yes" : "No"}
                </p>
              )}
            </>
          )}
          {validation && (
            <Alert role="alert" severity="error">
              {validation}
            </Alert>
          )}
          <div className="collection-dialog-buttons">
            <Button
              className="btn"
              onClick={() => close()}
              disabled={action.pending}
            >
              Cancel
            </Button>
            {needsQuantityStep &&
              removalStep === "quantity" &&
              !action.error && (
                <Button
                  className="btn"
                  onClick={(event) => changeRemovalStep(event, "choice")}
                  disabled={action.pending}
                >
                  Back
                </Button>
              )}
            {action.error ? (
              <Button
                className="btn collection-dialog-primary"
                variant="contained"
                sx={{ ml: "auto" }}
                onClick={action.retry}
                disabled={action.pending}
              >
                {action.rememberFailed
                  ? "Retry saving selection"
                  : "Retry original request"}
              </Button>
            ) : (
              <Button
                className="btn collection-dialog-primary"
                variant="contained"
                sx={{ ml: "auto" }}
                onClick={
                  showNext
                    ? (event) => changeRemovalStep(event, "quantity")
                    : form.handleSubmit(submit)
                }
                disabled={
                  action.pending ||
                  !settings?.ready ||
                  typeof syncWithDiscogs !== "boolean"
                }
              >
                {action.pending
                  ? "Saving…"
                  : showNext
                    ? "Next -->"
                    : dialog === "restore"
                      ? "Restore"
                      : dialog === "quantity"
                        ? "Save quantity"
                        : "Delete"}
              </Button>
            )}
          </div>
        </CollectionDialog>
      )}
    </div>
  );
}
